import React from 'react'
import { CREATE_MOCK_PROVIDERS } from '@dx-private/dxchart5-react-mock-providers'
import { LicensedChartWidgetHost } from './LicensedChartWidgetHost'
import { fetchChartFeedSymbols, resolveChartFeedHttpBaseUrl } from '../lib/chart-feed/client'
import { ChartFeedDataProvider } from '../lib/chart-feed/data-provider'
import type { ChartFeedConnectionStatus, ChartFeedPriceMessage, ChartFeedProvider } from '../lib/chart-feed/types'

interface Instrument {
  symbol: string
  description?: string
  type?: string
  priceIncrements: number[]
  tradingHours?: string
  tradable?: boolean
}

interface SymbolSuggestProvider {
  findInstrument(symbol: string): Promise<Instrument>
  searchInstruments(search: string): Promise<Instrument[]>
  onInstrumentChanged(symbol: string | undefined, chartId: string): void
}

class LandingPageSymbolSuggestProvider implements SymbolSuggestProvider {
  constructor(private readonly symbols: string[]) {}

  private toInstrument(symbol: string): Instrument {
    return {
      symbol,
      description: symbol,
      type: 'FOREX',
      priceIncrements: [0.00001],
      tradingHours: '',
      tradable: true,
    }
  }

  // Arrow methods preserve provider context when chart internals call callbacks unbound.
  findInstrument = async (symbol: string): Promise<Instrument> => {
    return this.toInstrument(symbol)
  }

  searchInstruments = async (search: string): Promise<Instrument[]> => {
    const query = search.trim().toLowerCase()

    return this.symbols
      .filter((symbol) => (query ? symbol.toLowerCase().includes(query) : true))
      .map((symbol) => this.toInstrument(symbol))
  }

  onInstrumentChanged = (_symbol: string | undefined, _chartId: string): void => {
    // no-op
  }
}

type SymbolLoadState = 'idle' | 'loading' | 'ready' | 'error'

const DEFAULT_SYMBOL = 'EUR/USD'

function sanitizeErrorMessage(message: string, token: string): string {
  const trimmedToken = token.trim()
  if (!trimmedToken) {
    return message
  }

  return message.split(trimmedToken).join('[REDACTED]')
}

function pickDefaultSymbol(symbols: string[]): string {
  if (symbols.includes(DEFAULT_SYMBOL)) {
    return DEFAULT_SYMBOL
  }

  return symbols[0] ?? DEFAULT_SYMBOL
}

export function ChartFeedLandingPage() {
  const [selectedProvider, setSelectedProvider] = React.useState<ChartFeedProvider>('dxfeed')
  const [basicToken, setBasicToken] = React.useState('')
  const [symbols, setSymbols] = React.useState<string[]>([])
  const [selectedSymbol, setSelectedSymbol] = React.useState(DEFAULT_SYMBOL)
  const [chartApi, setChartApi] = React.useState<any | null>(null)
  const [symbolLoadState, setSymbolLoadState] = React.useState<SymbolLoadState>('idle')
  const [symbolError, setSymbolError] = React.useState<string | null>(null)
  const [streamStatus, setStreamStatus] = React.useState<ChartFeedConnectionStatus>('idle')
  const [streamError, setStreamError] = React.useState<string | null>(null)
  const [latestTick, setLatestTick] = React.useState<ChartFeedPriceMessage | null>(null)
  const [hostError, setHostError] = React.useState<Error | null>(null)
  const trimmedBasicToken = basicToken.trim()

  React.useEffect(() => {
    if (!trimmedBasicToken) {
      setSymbols([])
      setSelectedSymbol(DEFAULT_SYMBOL)
      setSymbolLoadState('idle')
      setSymbolError(null)
      return
    }

    const abortController = new AbortController()
    setSymbolLoadState('loading')
    setSymbolError(null)

    fetchChartFeedSymbols(abortController.signal)
      .then((loadedSymbols) => {
        if (abortController.signal.aborted) {
          return
        }

        if (loadedSymbols.length === 0) {
          setSymbols([])
          setSelectedSymbol(DEFAULT_SYMBOL)
          setSymbolLoadState('error')
          setSymbolError('No symbols were returned for this token.')
          return
        }

        setSymbols(loadedSymbols)
        setSelectedSymbol(pickDefaultSymbol(loadedSymbols))
        setSymbolLoadState('ready')
      })
      .catch((error) => {
        if (abortController.signal.aborted) {
          return
        }

        setSymbols([])
        setSelectedSymbol(DEFAULT_SYMBOL)
        setSymbolLoadState('error')
        const message = error instanceof Error ? error.message : 'Unable to load symbols.'
        setSymbolError(sanitizeErrorMessage(message, trimmedBasicToken))
      })

    return () => {
      abortController.abort()
    }
  }, [trimmedBasicToken])

  const canStream = Boolean(trimmedBasicToken) && symbolLoadState === 'ready' && Boolean(selectedSymbol)

  const chartDataProvider = React.useMemo(() => {
    if (!canStream) {
      return null
    }

    return new ChartFeedDataProvider({
      basicToken,
      provider: selectedProvider,
      onStatusChange: (status) => {
        setStreamStatus(status)
      },
      onTick: (tick) => {
        setLatestTick(tick)
      },
      onStreamError: (message) => {
        setStreamError(sanitizeErrorMessage(message, basicToken))
      },
    })
  }, [basicToken, canStream, selectedProvider])

  const handleApiCreated = React.useCallback((api: any) => {
    setChartApi(api ?? null)
  }, [])

  React.useEffect(() => {
    return () => {
      chartDataProvider?.disconnect()
    }
  }, [chartDataProvider])

  React.useEffect(() => {
    if (!chartApi || !canStream || !selectedSymbol) {
      return
    }

    try {
      if (typeof chartApi.changeInstrument === 'function') {
        chartApi.changeInstrument(selectedSymbol)
      }
    } catch {
      // Ignore chart API errors so the page remains usable while reconnecting.
    }
  }, [canStream, chartApi, selectedSymbol])

  const mockProviders = React.useMemo(() => CREATE_MOCK_PROVIDERS() as any, [])

  const symbolSuggestProvider = React.useMemo(() => new LandingPageSymbolSuggestProvider(symbols), [symbols])

  const chartDependencies = React.useMemo(() => {
    if (!chartDataProvider) {
      return null
    }

    return {
      ...mockProviders,
      chartDataProvider,
      symbolSuggestProvider,
      initialInstrument: DEFAULT_SYMBOL,
      chartReactConfig: {
        ...(mockProviders.chartReactConfig ?? {}),
        trading: {
          ...(mockProviders.chartReactConfig?.trading ?? {}),
          enabled: false,
        },
      },
    }
  }, [chartDataProvider, mockProviders, symbolSuggestProvider])

  React.useEffect(() => {
    if (!chartDependencies) {
      setChartApi(null)
    }
  }, [chartDependencies])

  const selectedSymbolInOptions = symbols.length === 0 || symbols.includes(selectedSymbol)

  return (
    <main className="app-shell">
      <section className="control-panel">
        <h1>Chart Feed Landing Page</h1>
        <p className="panel-subtitle">Connect the licensed chart to the chart feed test endpoints.</p>

        <div className="control-grid">
          <label className="control-field">
            <span>Provider</span>
            <select
              value={selectedProvider}
              onChange={(event) => {
                const nextProvider = event.target.value as ChartFeedProvider
                setSelectedProvider(nextProvider)
                setStreamStatus('idle')
                setStreamError(null)
                setLatestTick(null)
              }}
            >
              <option value="dxfeed">dxfeed</option>
              <option value="twelveWS">twelveWS</option>
            </select>
          </label>

          <label className="control-field control-field--token">
            <span>Basic token</span>
            <input
              type="password"
              value={basicToken}
              onChange={(event) => {
                setBasicToken(event.target.value)
                setStreamStatus('idle')
                setLatestTick(null)
              }}
              autoComplete="off"
              spellCheck={false}
              placeholder="Enter backend basic token"
            />
          </label>

          <label className="control-field">
            <span>Symbol</span>
            <select
              value={selectedSymbolInOptions ? selectedSymbol : ''}
              onChange={(event) => {
                setSelectedSymbol(event.target.value)
                setStreamStatus('idle')
                setStreamError(null)
                setLatestTick(null)
              }}
              disabled={symbolLoadState !== 'ready' || symbols.length === 0}
            >
              {symbolLoadState !== 'ready' || symbols.length === 0 ? (
                <option value={selectedSymbol}>{selectedSymbol}</option>
              ) : null}
              {symbols.map((symbol) => (
                <option key={symbol} value={symbol}>
                  {symbol}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="status-grid">
          <article className="status-card">
            <h2>Connection Status</h2>
            <p>
              <strong>Symbols:</strong> {symbolLoadState}
            </p>
            <p>
              <strong>Stream:</strong> {streamStatus}
            </p>
            <p>
              <strong>Backend:</strong> {resolveChartFeedHttpBaseUrl()}
            </p>
          </article>

          <article className="status-card">
            <h2>Latest Tick</h2>
            <p>
              <strong>Provider:</strong> {latestTick?.provider ?? 'n/a'}
            </p>
            <p>
              <strong>Logical symbol:</strong> {latestTick?.logicalSymbol ?? 'n/a'}
            </p>
            <p>
              <strong>Provider symbol:</strong> {latestTick?.providerSymbol ?? 'n/a'}
            </p>
            <p>
              <strong>Basis:</strong> {latestTick?.basis ?? 'n/a'}
            </p>
            <p>
              <strong>Price:</strong> {typeof latestTick?.price === 'number' ? latestTick.price : 'n/a'}
            </p>
            <p>
              <strong>Received:</strong> {latestTick?.receivedAt ?? 'n/a'}
            </p>
          </article>
        </div>

        {symbolError ? <p className="status-message status-message--error">{symbolError}</p> : null}
        {streamError ? <p className="status-message status-message--error">{streamError}</p> : null}
        {hostError ? <p className="status-message status-message--error">{hostError.message}</p> : null}
      </section>

      <section className="chart-panel">
        {!chartDependencies ? (
          <div className="widget-status">Enter a valid Basic token and load symbols to start streaming.</div>
        ) : hostError ? (
          <div className="widget-status widget-status--error">{hostError.message}</div>
        ) : (
          <LicensedChartWidgetHost
            dependencies={chartDependencies}
            onApiCreated={handleApiCreated}
            onApiDestroyed={() => setChartApi(null)}
            onInitializationError={setHostError}
          />
        )}
      </section>
    </main>
  )
}
